import { useState, useCallback, useEffect, Suspense } from 'react'
import { Header } from '@/components/layout/Header.tsx'
import { MainLayout } from '@/components/layout/MainLayout.tsx'
import { Footer } from '@/components/layout/Footer.tsx'
import { ErrorBoundary } from '@/components/shared/ErrorBoundary.tsx'
import { LoadingFallback } from '@/components/shared/LoadingFallback.tsx'
import { FeedbackForm, FeedbackButton } from '@/components/FeedbackForm/FeedbackForm.tsx'
import { AboutModal, buildAboutConfig } from '@ascend/about-modal'
import appIdentity from 'virtual:app-identity'
import { useTheme } from '@/hooks/useTheme.ts'
import { useAppStore } from '@/store/useAppStore.ts'
import type { UserFeedback } from '@/utils/userFeedback.ts'

const aboutConfig = buildAboutConfig(appIdentity, 'VITE_APP_')

export default function App() {
  const theme = useTheme()
  const textSize = useAppStore((s) => s.preferences.textSize)
  const contrastMode = useAppStore((s) => s.preferences.contrastMode)

  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [aboutOpen, setAboutOpen] = useState(false)
  const [feedbackConfig, setFeedbackConfig] = useState<{
    type?: UserFeedback['type'] | undefined
    category?: string | undefined
    error?: string | undefined
  }>({})

  const handleOpenFeedback = useCallback((
    type?: UserFeedback['type'],
    category?: string,
    error?: string,
  ) => {
    setFeedbackConfig({ type: type ?? undefined, category: category ?? undefined, error: error ?? undefined })
    setFeedbackOpen(true)
  }, [])

  const handleCloseFeedback = useCallback(() => {
    setFeedbackOpen(false)
    setFeedbackConfig({ type: undefined, category: undefined, error: undefined })
  }, [])

  const handleOpenAbout = useCallback(() => setAboutOpen(true), [])
  const handleCloseAbout = useCallback(() => setAboutOpen(false), [])

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { error?: string; panelName?: string }
      handleOpenFeedback('bug_report', detail.panelName, detail.error)
    }
    window.addEventListener('app-error', handler)
    return () => window.removeEventListener('app-error', handler)
  }, [handleOpenFeedback])

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <div className="app" data-theme={theme} data-text-size={textSize} data-contrast={contrastMode}>
          <a href="#main-content" className="skip-link">Skip to main content</a>
          <Header onOpenAbout={handleOpenAbout} />
          <MainLayout />
          <Footer />
        </div>
        <FeedbackForm
          isOpen={feedbackOpen}
          onClose={handleCloseFeedback}
          initialType={feedbackConfig.type}
          initialCategory={feedbackConfig.category}
          initialError={feedbackConfig.error}
        />
        <FeedbackButton onOpen={handleOpenFeedback} />
        <AboutModal
          isOpen={aboutOpen}
          onClose={handleCloseAbout}
          config={aboutConfig}
        />
      </Suspense>
    </ErrorBoundary>
  )
}
